import { Loader2 } from 'lucide-react'

import { cn } from '@/lib/utils'

import { Button, useFrontendTheme } from '../../Theme'
import { LABELS } from '../../UiBlocks'
import { useCheckout } from '../state'

interface AddToCartButtonProps {
  /** Product SKU */
  sku: string
  /** Quantity to add (default: 1) */
  quantity?: number
  /** Disabled state */
  disabled?: boolean
  /** Button label (default: LABELS.ADD_TO_CART) */
  label?: string
  /** Custom className */
  className?: string
}

/**
 * AddToCartButton - Button component for adding products to cart
 *
 * Uses CheckoutProvider context for cart operations.
 * Shows loading spinner when adding, label-only when idle.
 *
 * @example
 * // Inside a component wrapped with CheckoutProvider
 * <AddToCartButton sku="SKU123" />
 */
export function AddToCartButton({
  sku,
  quantity = 1,
  disabled = false,
  label = LABELS.ADD_TO_CART,
  className,
}: AddToCartButtonProps) {
  const { handleAddToCart, addingToCart } = useCheckout()
  const { addToCartLabelVariants } = useFrontendTheme()

  const isLoading = addingToCart === sku

  const handleClick = () => handleAddToCart({ sku, quantity })

  return (
    <Button
      variant="addToCart"
      size="addToCart"
      onClick={handleClick}
      disabled={isLoading || disabled}
      className={cn('relative', className)}
    >
      {/* Spinner overlay - appears on top when loading */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="animate-spin w-6 h-6" aria-hidden="true" />
        </div>
      )}
      {/* Content - stays in place but becomes invisible when loading */}
      <div className={cn('flex items-center justify-center', isLoading && 'invisible')}>
        <span className={addToCartLabelVariants()}>{label}</span>
      </div>
    </Button>
  )
}
